import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Event } from '../api/events';

interface SessionsContextProps {
  sessions: Event[];
  resetKey: number;
  cancelTargetSession: Event | null;
  setSessions: Dispatch<SetStateAction<Event[]>>;
  setCancelTargetSession: Dispatch<SetStateAction<Event | null>>;
  reset: () => void;
}

interface SessionsProviderProps {
  children: ReactNode;
}

export const SessionsContext = createContext<SessionsContextProps>({
  sessions: [],
  resetKey: 0,
  cancelTargetSession: null,
  setSessions: () => {},
  setCancelTargetSession: () => {},
  reset: () => {},
});

export const SessionsProvider = ({ children }: SessionsProviderProps) => {
  const [sessions, setSessions] = useState<Event[]>([]);
  const [cancelTargetSession, setCancelTargetSession] = useState<Event | null>(
    null,
  );
  const [resetKey, setResetKey] = useState(0);

  const reset = useCallback(() => setResetKey((prev) => prev + 1), []);

  const contextValue: SessionsContextProps = {
    sessions,
    resetKey,
    cancelTargetSession,
    setSessions,
    setCancelTargetSession,
    reset,
  };

  return (
    <SessionsContext.Provider value={contextValue}>
      {children}
    </SessionsContext.Provider>
  );
};

export const useSessionsContext = () => {
  const context = useContext(SessionsContext);
  if (!context) {
    throw new Error(
      'useSessionsContext must be used within a SessionsProvider',
    );
  }
  return context;
};
